import { logger } from '@/config/logger'
import { useEffect, useRef, useState } from 'react'
import { BiSearch } from 'react-icons/bi'
import { useOutsideClick } from '../utils'

interface AutoCompleteProps {
  id?: string
  placeholder?: string
  leftIcon?: 'search'
  options?: {
    value: string
    title: string
  }[]
  handleChange: (e: any) => void
  name: string
}

export const AutoComplete = (props: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)

  const [isFocused, setIsFocused] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined
  )
  const [hoveredIndex, setHoveredIndex] = useState(-1)
  const [shouldFilter, setShouldFilter] = useState(true)

  const filteredOptions = props.options?.filter((elt) =>
    search.length > 0 && shouldFilter
      ? elt.title.toLowerCase().startsWith(search.toLowerCase())
      : true
  )

  useOutsideClick(
    inputRef.current,
    () => {
      if (isFocused) {
        const optionIndex = props.options?.findIndex(
          (elt) => elt.title === search
        )
        if (optionIndex === -1) {
          const lastOptionIndex = props.options?.findIndex(
            (elt) => elt.value === selectedValue
          )
          if (lastOptionIndex && lastOptionIndex > -1) {
            if (props.options && search.length > 0) {
              setSearch(props.options[lastOptionIndex].title)
              setShouldFilter(false)
            } else {
              setSearch('')
              setSelectedValue(undefined)
            }
          } else {
            setSearch('')
          }
        }
        setIsFocused(false)
        setHoveredIndex(-1)
      }
    },
    [isFocused, search, selectedValue]
  )

  useEffect(() => {
    setHoveredIndex(-1)
  }, [filteredOptions?.length])

  useEffect(() => {
    if (isFocused) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (filteredOptions) {
          switch (e.key) {
            case 'ArrowDown':
              setHoveredIndex((previousState) =>
                previousState < filteredOptions.length - 1
                  ? previousState + 1
                  : filteredOptions.length - 1
              )
              break
            case 'ArrowUp':
              setHoveredIndex((previousState) =>
                previousState > 0 ? previousState - 1 : 0
              )
              break

            default:
            // Do nothing
          }
        }
      }
      document.addEventListener('keydown', handleKeyDown, false)
      return () => {
        document.removeEventListener('keydown', handleKeyDown, false)
      }
    } else {
      setHoveredIndex(-1)
    }
  }, [isFocused, filteredOptions?.length])

  const onItemClick = (elt: { value: string; title: string }) => {
    setSearch(elt.title)
    setSelectedValue(elt.value)
    setShouldFilter(false)
  }

  useEffect(() => {
    if (isFocused) {
      if (hoveredIndex > -1) {
        optionsRef.current?.scrollTo(0, (hoveredIndex - 5) * 36)
      }
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          if (filteredOptions?.[hoveredIndex] !== undefined) {
            onItemClick(filteredOptions?.[hoveredIndex])
            setIsFocused(false)
            inputRef.current?.blur()
          }
        }
      }
      document.addEventListener('keydown', handleKeyDown, false)
      return () => {
        document.removeEventListener('keydown', handleKeyDown, false)
      }
    }
  }, [isFocused, hoveredIndex, filteredOptions?.length])

  useEffect(() => {
    props.handleChange({
      target: { name: props.name, value: selectedValue ?? 'None' },
    })
  }, [selectedValue])

  return (
    <div className="relative mt-[15px] mx-[5px]">
      <div className="relative">
        <input
          id={props.id}
          ref={inputRef}
          className="py-[8px] pl-[42px] pr-[20px] border-solid border-[1px] border-[var(--primary)] rounded-[8px] min-w-[300px] focus:border-transparent focus:ring-0 focus:border-[var(--primary)] placeholder-gray-400"
          onFocus={() => setIsFocused(true)}
          placeholder={props.placeholder}
          value={search}
          onChange={(e) => {
            logger.debug('value:', e.target.value)
            setSearch(e.target.value)
            setShouldFilter(true)
          }}
        />
        {props.leftIcon === 'search' && (
          <BiSearch
            size={24}
            className="absolute top-[9px] left-[9px]"
            onClick={() => inputRef.current?.focus()}
          />
        )}
      </div>
      {isFocused && (
        <div className="absolute z-10 border-solid border-[1px] border-[var(--primary)] rounded-[8px] min-w-[300px] bg-white top-[50px] overflow-hidden">
          <div ref={optionsRef} className="max-h-[218px] overflow-x-scroll">
            {filteredOptions?.map((elt, index) => (
              <p
                key={elt.value}
                className={`cursor-pointer px-[12px] py-[6px] hover:bg-[--accentColor] ${hoveredIndex === index && 'bg-[--accentColor]'}`}
                onClick={() => onItemClick(elt)}
                onMouseEnter={() => setHoveredIndex(-1)}
              >
                {elt.title}
              </p>
            ))}
            {filteredOptions?.length === 0 && (
              <p className="px-[12px] py-[6px] text-gray-400">Not found</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
